import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

const STORAGE_KEY = 'rabbithole_v1';
const ARCHIVE_KEY = 'rabbithole_archive_v1';
function loadState() { try { const r = localStorage.getItem(STORAGE_KEY); if (r) return JSON.parse(r); } catch {} return null; }
function saveState(h) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(h)); } catch {} }
function loadArchive() { try { const r = localStorage.getItem(ARCHIVE_KEY); if (r) return JSON.parse(r); } catch {} return []; }
function saveArchive(a) { try { localStorage.setItem(ARCHIVE_KEY, JSON.stringify(a)); } catch {} }
function uid() { return Math.random().toString(36).slice(2, 10); }

function parseBody(raw) {
  const parts = []; const re = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;
  let last = 0, m;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) parts.push({ type: 'text', value: raw.slice(last, m.index) });
    parts.push({ type: 'link', topic: m[1].trim(), label: (m[2] || m[1]).trim() });
    last = re.lastIndex;
  }
  if (last < raw.length) parts.push({ type: 'text', value: raw.slice(last) });
  return parts;
}

function newHole(id) {
  return { id: id || uid(), title: 'New Rabbit Hole', burrows: [], currentId: null, createdAt: Date.now() };
}

const RANDOM_TOPICS = [
  'How do black holes form', 'What caused the fall of Rome', 'How does memory work',
  'What is dark matter', 'How did language evolve', 'What is consciousness',
  'How do vaccines work', 'What is the Fermi paradox', 'How does the internet work',
  'What caused World War I', 'How do dreams work', 'What is quantum entanglement',
  'How did life begin', 'How does evolution work', 'What is the gut microbiome',
  'How do neurons fire', 'What is inflation', 'How was the universe created',
  'What is CRISPR', 'How does DNA work', 'What is the stock market',
  'How does the brain store memories', 'What is string theory', 'How do planes fly',
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

function getSuggestions(allBurrows) {
  const recent = allBurrows.slice(-10).map(b => b.topic.toLowerCase());
  return shuffle(RANDOM_TOPICS).filter(t => !recent.includes(t.toLowerCase())).slice(0, 8);
}

// Build the ancestor trail for a given burrow (for context)
function buildTrail(burrows, currentId) {
  const byId = {};
  burrows.forEach(b => { byId[b.id] = b; });
  const trail = [];
  let cur = byId[currentId];
  while (cur) {
    // Build a rich summary of everything in this burrow
    const aliceLog = (cur.aliceConversation || [])
      .map(m => (m.role === 'user' ? 'Q: ' : 'A: ') + m.text)
      .join(' | ');
    const focusLog = cur.aliceFocus
      ? `Focus: ${cur.aliceFocus.topic} — ${cur.aliceFocus.layers.join(' ')}`
      : '';
    trail.unshift({
      topic: cur.topic,
      body: cur.body || '',
      aliceLog: aliceLog || '',
      focusLog: focusLog || '',
    });
    cur = cur.parentId ? byId[cur.parentId] : null;
  }
  return trail;
}

// ── Streaming fetch ───────────────────────────────────────────────────────────
async function streamAsk(topic, trail, onChunk, onDone, onError, aliceMode = false) {
  try {
    const token = localStorage.getItem('rh_token');
    const headers = { 'content-type': 'application/json' };
    if (token) headers['authorization'] = 'Bearer ' + token;
    const res = await fetch('https://rabbithole-api.onrender.com/api/ask', {
      method: 'POST',
      headers,
      body: JSON.stringify({ topic, trail, aliceMode }),
    });

    if (!res.ok) { onError('Server error'); return; }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    let sources = [];

    let finished = false;
    const processChunks = async () => {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (!finished) { finished = true; onDone(fullText, sources); }
          return;
        }
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const raw = line.slice(6).trim();
            if (raw === '[DONE]') {
              if (!finished) { finished = true; onDone(fullText, sources); }
              return;
            }
            try {
              const parsed = JSON.parse(raw);
              if (parsed.error) { onError(parsed.error); return; }
              if (parsed.sources) { sources = parsed.sources; }
              if (parsed.text) {
                fullText += parsed.text;
                onChunk(fullText);
              }
            } catch {}
          }
        }
      }
    };

    await processChunks();
  } catch (e) {
    onError(e.message);
  }
}

async function apiFetch(path, body, token) {
  const headers = { 'content-type': 'application/json' };
  if (token) headers['authorization'] = 'Bearer ' + token;
  const res = await fetch('https://rabbithole-api.onrender.com' + path, { method: 'POST', headers, body: JSON.stringify(body) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error');
  return data;
}

// ── Warren Manager ───────────────────────────────────────────────────────────
// ── Menu Panel ───────────────────────────────────────────────────────────────
function MenuPanel({ holes, activeHole, archive, onSwitch, onCloseHole, onRestore, onDeletePermanent, onViewWarren, onClose, onRename, onNewHole, user, onUpgrade, onLogout, onLogin }) {
  const [tab, setTab] = useState('open');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [sessionSummary, setSessionSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const otherHoles = holes.filter(h => h.id !== activeHole.id);
  return (
    <div style={MP.overlay} onClick={onClose}>
      <div style={MP.sheet} onClick={e => e.stopPropagation()}>
        <div style={MP.header}>
          <div style={MP.title}>Menu</div>
          <button style={MP.closeBtn} onClick={onClose}>✕</button>
        </div>
        {user ? (
          <div style={{ padding: '10px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 13, color: '#ffffff', fontFamily: 'Inter, sans-serif' }}>{user.email}</div>
              <div style={{ fontSize: 11, color: user.tier === 'meridian' ? '#00b359' : '#555555', fontFamily: 'Inter, sans-serif', marginTop: 2 }}>{user.tier === 'meridian' ? '✦ Meridian' : 'Free'}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {user.tier !== 'meridian' && (
                <>
                  <button onClick={onUpgrade} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 8, border: '1px solid #00b359', background: 'transparent', color: '#00b359', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#00b359'; e.currentTarget.style.color = '#00b359'; }}
                  >Upgrade ✦</button>
                  <div style={{ fontSize: 10, color: '#555555', marginTop: 6, fontFamily: 'Inter, sans-serif', lineHeight: 1.4 }}>⚠️ Early access — Alice Mode and academic search are still in development. Purchase at your own discretion.</div>
                </>
              )}
              <button onClick={onLogout} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 8, border: '1px solid #2a2a2a', background: 'transparent', color: '#555555', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#555555'; }}
              >Sign out</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '10px 24px 0' }}>
            <button onClick={onLogin} style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: 13, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}>Sign in or create account</button>
          </div>
        )}

        <div style={MP.tabs}>
          <button style={{ ...MP.tabBtn, ...(tab === 'open' ? MP.tabActive : {}) }} onClick={() => setTab('open')}
            onMouseEnter={e => { if (tab !== 'open') { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}}
            onMouseLeave={e => { if (tab !== 'open') { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}}>
            Your Rabbit Holes <span style={MP.badge}>{holes.length}</span>
          </button>
          <button style={{ ...MP.tabBtn, ...(tab === 'archived' ? MP.tabActive : {}) }} onClick={() => setTab('archived')}
            onMouseEnter={e => { if (tab !== 'archived') { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}}
            onMouseLeave={e => { if (tab !== 'archived') { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}}>
            Archived <span style={MP.badge}>{archive.length}</span>
          </button>
        </div>
        {tab === 'open' && (
          <div style={MP.list}>
            {holes.length === 0 ? <div style={MP.empty}>No rabbit holes yet.</div>
            : holes.map(h => (
              <div key={h.id} style={{ ...MP.item, cursor: 'pointer', borderColor: h.id === activeHole.id ? '#00ff88' : '#2a2a2a' }}
                onClick={() => { if (editingId !== h.id) onSwitch(h.id); }}
                onContextMenu={e => { e.preventDefault(); setEditingId(h.id); setEditValue(h.title); }}
                onTouchStart={e => {
                  const t = setTimeout(() => { setEditingId(h.id); setEditValue(h.title); }, 600);
                  e.currentTarget._pressTimer = t;
                }}
                onTouchEnd={e => { clearTimeout(e.currentTarget._pressTimer); }}
                onTouchMove={e => { clearTimeout(e.currentTarget._pressTimer); }}
                onMouseEnter={e => { if (editingId !== h.id) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; setHoveredId(h.id); }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = h.id === activeHole.id ? '#00ff88' : '#2a2a2a'; setHoveredId(null); }}
              >
                <div style={MP.itemLeft}>
                  {editingId === h.id ? (
                    <input
                      autoFocus
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onBlur={() => { if (editValue.trim()) onRename(h.id, editValue.trim()); setEditingId(null); }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { if (editValue.trim()) onRename(h.id, editValue.trim()); setEditingId(null); }
                        if (e.key === 'Escape') setEditingId(null);
                        e.stopPropagation();
                      }}
                      style={{ fontSize: 14, fontWeight: 500, background: 'transparent', border: 'none', borderBottom: '1px solid #00b359', color: '#ffffff', outline: 'none', width: '100%', padding: '2px 0', fontFamily: 'Inter, sans-serif' }}
                    />
                  ) : (
                    <div style={MP.itemTitle}>{h.title}</div>
                  )}
                  <div style={MP.itemMeta}>{h.burrows.length} {h.burrows.length === 1 ? 'burrow' : 'burrows'}</div>
                  <div style={{ ...MP.itemBurrows, transition: 'all 0.2s' }}>
                    {h.burrows.slice(0, hoveredId === h.id ? 10 : 3).map(b => <span key={b.id} style={MP.burrowPill}>{b.label || b.topic}</span>)}
                    {h.burrows.length > (hoveredId === h.id ? 10 : 3) && <span style={MP.burrowMore}>+{h.burrows.length - (hoveredId === h.id ? 10 : 3)} more</span>}
                  </div>
                </div>
                <span style={{ fontSize: 14, color: '#444444', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, border: '1px solid transparent', flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); onCloseHole(h.id); }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#444444'; e.currentTarget.style.borderColor = 'transparent'; }}
                >✕</span>
              </div>
            ))}
          </div>
        )}
        {tab === 'archived' && (
          <div style={MP.list}>
            {archive.length === 0 ? <div style={MP.empty}>No archived holes yet.</div>
            : [...archive].reverse().map(h => (
              <div key={h.id} style={MP.item}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; setHoveredId(h.id); }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; setHoveredId(null); }}
              >
                <div style={MP.itemLeft}>
                  <div style={MP.itemTitle}>{h.title}</div>
                  <div style={MP.itemMeta}>{h.burrows.length} {h.burrows.length === 1 ? 'burrow' : 'burrows'}{h.deletedAt && <span> · deleted {new Date(h.deletedAt).toLocaleDateString()}</span>}</div>
                  <div style={{ ...MP.itemBurrows, transition: 'all 0.2s' }}>
                    {h.burrows.slice(0, hoveredId === h.id ? 10 : 3).map(b => <span key={b.id} style={MP.burrowPill}>{b.label || b.topic}</span>)}
                    {h.burrows.length > (hoveredId === h.id ? 10 : 3) && <span style={MP.burrowMore}>+{h.burrows.length - (hoveredId === h.id ? 10 : 3)} more</span>}
                  </div>
                </div>
                <div style={MP.itemActions}>
                  <button style={MP.restoreBtn} onClick={() => onRestore(h)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#00ff88'; e.currentTarget.style.color = '#00ff88'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}
                  >Restore</button>
                  <button style={MP.deleteBtn} onClick={() => onDeletePermanent(h.id)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff3333'; e.currentTarget.style.color = '#ff3333'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#555555'; }}
                  >Delete forever</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === 'open' && <div style={MP.newHoleBar}>
          <button style={MP.newHoleFullBtn} onClick={onNewHole}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}
          >+ New Rabbit Hole</button>
        </div>}
      </div>
    </div>
  );
}
const MP = {
  overlay: { position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '54px 20px 0', animation: 'fadeIn 0.2s ease' },
  sheet: { width: '95vw', maxWidth: 400, background: '#1a1a1a', borderRadius: 16, border: '1px solid #2a2a2a', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', maxHeight: '80vh', animation: 'grow 0.25s cubic-bezier(0.4,0,0.2,1)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' },
  title: { fontSize: 20, fontWeight: 600, color: '#ffffff', fontFamily: "'Playfair Display', serif" },
  closeBtn: { fontSize: 18, background: 'none', border: 'none', color: '#555555', cursor: 'pointer', padding: '0 4px' },
  newBtn: { fontSize: 13, padding: '6px 14px', borderRadius: 10, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', transition: 'all 0.15s' },
  newHoleBar: { padding: '12px 16px 16px', borderTop: '1px solid #2a2a2a', flexShrink: 0 },
  newHoleFullBtn: { width: '100%', fontSize: 14, padding: '12px', borderRadius: 10, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s' },
  tabs: { display: 'flex', gap: 6, padding: '14px 20px 0' },
  tabBtn: { fontSize: 12, padding: '5px 12px', borderRadius: 20, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' },
  tabActive: { background: 'transparent', color: '#ffffff', borderColor: '#00b359' },
  badge: { fontSize: 10, background: '#2a2a2a', color: '#666666', padding: '1px 5px', borderRadius: 8 },
  list: { overflowY: 'auto', padding: '10px 16px 10px', flex: 1, minHeight: 0 },
  item: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px', borderRadius: 10, border: '1px solid #2a2a2a', marginBottom: 8, gap: 12, transition: 'border-color 0.15s' },
  itemLeft: { flex: 1, minWidth: 0 },
  itemTitle: { fontSize: 14, fontWeight: 500, color: '#ffffff', marginBottom: 3 },
  itemMeta: { fontSize: 11, color: '#555555', marginBottom: 6 },
  itemBurrows: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  burrowPill: { fontSize: 10, color: '#00b359', background: 'rgba(0,179,89,0.08)', border: '1px solid rgba(0,179,89,0.15)', padding: '1px 6px', borderRadius: 10 },
  burrowMore: { fontSize: 10, color: '#444444', padding: '1px 6px' },
  itemActions: { display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 },
  restoreBtn: { fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  deleteBtn: { fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #2a2a2a', background: 'transparent', color: '#555555', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  empty: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#444444', fontStyle: 'italic', padding: '32px 20px', textAlign: 'center' },
};


// ── Combined Warren Panel ────────────────────────────────────────────────────
function WarrenPanel({ activeHole, holes, archive, onJump, onSwitch, onRestore, onDeletePermanent, onClose, onViewWarren, onRenameHole, onCloseHole, onDeleteBurrow }) {
  const [tab, setTab] = useState('current');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [sessionSummary, setSessionSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const { burrows, currentId } = activeHole;
  const dragRef = useRef(null);
  const mapRef = useRef(null);

  // Auto-fit map on open
  useEffect(() => {
    if (!mapRef.current || burrows.length === 0) return;
    const rect = mapRef.current.getBoundingClientRect();
    const vw = rect.width || window.innerWidth;
    const vh = rect.height || window.innerHeight * 0.8;
    // Find bounds of all positioned nodes
    const posVals = Object.values(positions);
    if (posVals.length === 0) return;
    const xs = posVals.map(p => p.x);
    const ys = posVals.map(p => p.y);
    const minX = Math.min(...xs) - 60;
    const maxX = Math.max(...xs) + 60;
    const minY = Math.min(...ys) - 60;
    const maxY = Math.max(...ys) + 60;
    const contentW = maxX - minX;
    const contentH = maxY - minY;
    const scale = Math.min(vw / contentW, vh / contentH, 1.4);
    const tx = (vw - contentW * scale) / 2 - minX * scale;
    const ty = (vh - contentH * scale) / 2 - minY * scale;
    setTransform({ x: tx, y: ty, scale });
    setMapReady(true);
  }, []);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [mapReady, setMapReady] = useState(false);
  const total = burrows.length;

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const handler = e => {
      e.preventDefault();
      e.stopPropagation();
      if (e.ctrlKey || e.metaKey) {
        const rect = el.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        setTransform(t => {
          const newScale = Math.min(2.5, Math.max(0.3, t.scale - e.deltaY * 0.01));
          const ratio = newScale / t.scale;
          return { scale: newScale, x: cx - ratio * (cx - t.x), y: cy - ratio * (cy - t.y) };
        });
      } else {
        setTransform(t => ({ ...t, x: t.x - e.deltaX, y: t.y - e.deltaY }));
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [mapRef.current]);

  // Build radial tree
  const byId = {};
  burrows.forEach(b => { byId[b.id] = { ...b, children: [] }; });
  const roots = [];
  burrows.forEach(b => {
    if (b.parentId && byId[b.parentId]) byId[b.parentId].children.push(byId[b.id]);
    else roots.push(byId[b.id]);
  });

  const R_STEP = 220; // radius per depth level
  const NODE_R = 58;  // node circle radius
  const positions = {};

  // Count leaves for angle allocation
  function countLeaves(node) {
    if (node.children.length === 0) return 1;
    return node.children.reduce((s, c) => s + countLeaves(c), 0);
  }

  const totalLeaves = roots.reduce((s, r) => s + countLeaves(r), 0);
  const CX = 400, CY = 400; // center of radial map

  function radialLayout(node, depth, angleStart, angleEnd) {
    const angle = (angleStart + angleEnd) / 2;
    const r = depth === 0 ? 0 : depth * R_STEP;
    positions[node.id] = {
      x: CX + r * Math.cos(angle),
      y: CY + r * Math.sin(angle),
      angle,
    };
    let childAngle = angleStart;
    node.children.forEach(child => {
      const leaves = countLeaves(child);
      const childSpan = (angleEnd - angleStart) * (leaves / Math.max(countLeaves(node), 1));
      radialLayout(child, depth + 1, childAngle, childAngle + childSpan);
      childAngle += childSpan;
    });
  }

  if (roots.length === 1) {
    radialLayout(roots[0], 0, -Math.PI, Math.PI);
  } else {
    let a = -Math.PI;
    roots.forEach(r => {
      const leaves = countLeaves(r);
      const span = 2 * Math.PI * (leaves / totalLeaves);
      radialLayout(r, 0, a, a + span);
      a += span;
    });
  }

  const pathIds = new Set();
  let cur = byId[currentId];
  while (cur) { pathIds.add(cur.id); cur = cur.parentId ? byId[cur.parentId] : null; }

  const edges = burrows
    .filter(b => b.parentId && positions[b.parentId] && positions[b.id])
    .map(b => {
      const p = positions[b.parentId], c = positions[b.id];
      const onPath = pathIds.has(b.id) && pathIds.has(b.parentId);
      return { x1: p.x, y1: p.y, x2: c.x, y2: c.y, onPath };
    });

  const W = 800;
  const H = 800;

  const onMouseDown = e => { dragRef.current = { sx: e.clientX, sy: e.clientY, tx: transform.x, ty: transform.y }; };
  const onMouseMove = e => { if (!dragRef.current) return; const { tx, ty, sx, sy } = dragRef.current; const dx = e.clientX - sx; const dy = e.clientY - sy; setTransform(t => ({ ...t, x: tx + dx, y: ty + dy })); };
  const onMouseUp = () => { dragRef.current = null; };
  const onWheel = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) {
      // Pinch zoom — zoom relative to center of the map element
      const rect = e.currentTarget.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setTransform(t => {
        const newScale = Math.min(2.5, Math.max(0.3, t.scale - e.deltaY * 0.01));
        const ratio = newScale / t.scale;
        return {
          scale: newScale,
          x: cx - ratio * (cx - t.x),
          y: cy - ratio * (cy - t.y),
        };
      });
    } else {
      // Two-finger pan
      setTransform(t => ({ ...t, x: t.x - e.deltaX, y: t.y - e.deltaY }));
    }
  };

  const otherHoles = holes.filter(h => h.id !== activeHole.id);

  return (
    <div style={WP.overlay} onClick={onClose}>
      <div style={WP.sheet} onClick={e => e.stopPropagation()}>

        {/* Session summary */}
        {sessionSummary && (
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #0f0f0f', background: '#030303' }}>
            <div style={{ fontSize: 9, letterSpacing: 3, color: '#333333', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', marginBottom: 8 }}>◈ Session summary</div>
            <p style={{ fontSize: 13, color: '#888888', lineHeight: 1.8, fontFamily: 'Inter, sans-serif', margin: 0 }}>{sessionSummary}</p>
            <button onClick={() => setSessionSummary('')} style={{ marginTop: 10, fontSize: 10, color: '#333333', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif', letterSpacing: 1 }}>dismiss</button>
          </div>
        )}

        {/* Header */}
        <div style={{ ...WP.header, padding: '16px 24px', flexDirection: 'row-reverse' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            {editingId === activeHole.id ? (
              <input
                autoFocus
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={() => { onRenameHole(activeHole.id, editValue); setEditingId(null); }}
                onKeyDown={e => { if (e.key === 'Enter') { onRenameHole(activeHole.id, editValue); setEditingId(null); } if (e.key === 'Escape') setEditingId(null); }}
                style={{ fontSize: 20, fontWeight: 600, fontFamily: "'Playfair Display', serif", background: 'transparent', border: 'none', borderBottom: '1px solid #00b359', color: '#ffffff', outline: 'none', width: '100%', padding: '2px 0' }}
              />
            ) : (
              <div style={{ ...WP.headerTitle }}
                onContextMenu={e => { e.preventDefault(); setEditingId(activeHole.id); setEditValue(activeHole.title); }}
                onTouchStart={e => {
                  const t = setTimeout(() => { setEditingId(activeHole.id); setEditValue(activeHole.title); }, 600);
                  e.currentTarget._pressTimer = t;
                }}
                onTouchEnd={e => { clearTimeout(e.currentTarget._pressTimer); }}
                onTouchMove={e => { clearTimeout(e.currentTarget._pressTimer); }}
              style={{ display: 'none' }}>{activeHole.title}</div>
            )}
          </div>
          <button style={WP.closeBtn} onClick={onClose}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#1a1a1a'; e.currentTarget.style.color = '#333333'; }}
          >Close</button>
          <button
            style={{ fontSize: 11, background: 'none', border: '1px solid #1a1a1a', borderRadius: 20, color: '#333333', cursor: 'pointer', padding: '6px 16px', fontFamily: 'Inter, sans-serif', letterSpacing: '1px', textTransform: 'uppercase', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#00ff88'; e.currentTarget.style.color = '#00ff88'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#1a1a1a'; e.currentTarget.style.color = '#333333'; }}
            onClick={async () => {
              if (summaryLoading) return;
              setSummaryLoading(true);
              const allText = burrows.map(b => `Topic: ${b.topic}\n${b.body ? b.body.slice(0,300).replace(/\[\[.*?\]\]/g,'') : ''}${(b.aliceConversation||[]).filter(m=>m.role==='alice').map(m=>m.text).join(' ')}`).join('\n\n');
              try {
                const r = await fetch('https://rabbithole-api.onrender.com/api/summarize', {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ content: allText.slice(0, 3000) })
                });
                const d = await r.json();
                setSessionSummary(d.summary || '');
              } catch {}
              setSummaryLoading(false);
            }}
          >{summaryLoading ? '...' : '◈ What did I learn?'}</button>
        </div>



        {/* Current hole map */}
        {tab === 'current' && (
          <>

            {total === 0 ? (
              <div style={WP.empty}>Start digging to see your warren grow.</div>
            ) : (
              <div ref={mapRef} style={WP.mapWrap} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} onWheel={onWheel} onTouchStart={e => {
                  e.preventDefault();
                  if (e.touches.length === 1) {
                    dragRef.current = { sx: e.touches[0].clientX, sy: e.touches[0].clientY, tx: transform.x, ty: transform.y, dist: null };
                  } else if (e.touches.length === 2) {
                    const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
                    dragRef.current = { sx: (e.touches[0].clientX + e.touches[1].clientX)/2, sy: (e.touches[0].clientY + e.touches[1].clientY)/2, tx: transform.x, ty: transform.y, dist, scale: transform.scale };
                  }
                }}
                onTouchMove={e => {
                  e.preventDefault();
                  if (!dragRef.current) return;
                  const dr = dragRef.current;
                  if (e.touches.length === 1 && dr.dist === null) {
                    const dx = e.touches[0].clientX - dr.sx;
                    const dy = e.touches[0].clientY - dr.sy;
                    const tx = dr.tx, ty = dr.ty;
                    setTransform(t => ({ ...t, x: tx + dx, y: ty + dy }));
                  } else if (e.touches.length === 2 && dr.dist !== null) {
                    const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
                    const newScale = Math.min(2.5, Math.max(0.3, dr.scale * (dist / dr.dist)));
                    const ratio = newScale / dr.scale;
                    const mx = (e.touches[0].clientX + e.touches[1].clientX)/2;
                    const my = (e.touches[0].clientY + e.touches[1].clientY)/2;
                    const { tx, ty, sx, sy } = dr;
                    setTransform({ scale: newScale, x: mx - ratio * (sx - tx), y: my - ratio * (sy - ty) });
                  }
                }}
                onTouchEnd={() => { dragRef.current = null; }}>
                <svg width={W} height={H} style={{ transform: `translate(${transform.x}px,${transform.y}px) scale(${transform.scale})`, transformOrigin: '0 0', cursor: 'grab', display: 'block', overflow: 'visible' }}>
                  {/* Radial ring guides */}
                  <circle cx={CX} cy={CY} r={R_STEP} fill='none' stroke='rgba(255,255,255,0.015)' strokeWidth={1} />
                  <circle cx={CX} cy={CY} r={R_STEP*2} fill='none' stroke='rgba(255,255,255,0.01)' strokeWidth={1} />
                  <circle cx={CX} cy={CY} r={R_STEP*3} fill='none' stroke='rgba(255,255,255,0.008)' strokeWidth={1} />
                  <circle cx={CX} cy={CY} r={12} fill='rgba(0,255,136,0.05)' stroke='rgba(0,255,136,0.15)' strokeWidth={1} />
                  <defs>
                    <marker id="arrowOn" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3 z" fill="rgba(0,255,136,0.5)" /></marker>
                    <marker id="arrowOff" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3 z" fill="#333" /></marker>
                  </defs>
                  {edges.map((e, i) => (
                    <line key={i} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} stroke={e.onPath ? 'rgba(0,255,136,0.6)' : 'rgba(255,255,255,0.12)'} strokeWidth={e.onPath ? 1.5 : 0.8} />
                  ))}
                  {burrows.map(b => {
                    const pos = positions[b.id];
                    if (!pos) return null;
                    const isCurrent = b.id === currentId;
                    const isOnPath = pathIds.has(b.id);
                    const hasAlice = (b.aliceConversation && b.aliceConversation.length > 0) || b.aliceFocus;
                    const words = (b.label || b.topic).split(' ');
                    // Split label into 2 lines max
                    const mid = Math.ceil(words.length / 2);
                    const line1 = words.slice(0, mid).join(' ');
                    const line2 = words.slice(mid).join(' ');
                    const r = NODE_R + (isCurrent ? 6 : 0);
                    const nodeColor = isCurrent ? '#00ff88' : isOnPath ? 'rgba(0,255,136,0.5)' : '#1f1f1f';
                    const strokeColor = isCurrent ? '#00ff88' : isOnPath ? 'rgba(0,255,136,0.5)' : '#404040';
                    return (
                      <g key={b.id} style={{ cursor: 'pointer' }}
                        onMouseEnter={e => {
                          setSelectedNode(b.id);
                          const ring = e.currentTarget.querySelector('.sel-ring'); if (ring) ring.setAttribute('opacity', '1');
                          const x = e.currentTarget.querySelector('.del-btn'); if (x) x.setAttribute('opacity', '1');
                          e.currentTarget.querySelectorAll('.node-label').forEach(t => { t.setAttribute('font-size', '22'); t.setAttribute('fill', '#ffffff'); t.setAttribute('font-weight', '700'); });
                        }}
                        onMouseLeave={e => {
                          setSelectedNode(null);
                          const ring = e.currentTarget.querySelector('.sel-ring'); if (ring) ring.setAttribute('opacity', '0');
                          const x = e.currentTarget.querySelector('.del-btn'); if (x) x.setAttribute('opacity', '0');
                          e.currentTarget.querySelectorAll('.node-label').forEach(t => { t.setAttribute('font-size', isCurrent ? '13' : '11'); t.setAttribute('fill', isCurrent ? '#00ff88' : isOnPath ? '#cccccc' : '#888888'); t.setAttribute('font-weight', '500'); });
                        }}
                      >
                        {/* Outer glow ring for current */}
                        {isCurrent && <circle cx={pos.x} cy={pos.y} r={r + 20} fill="none" stroke="rgba(0,255,136,0.06)" strokeWidth={20} />}
                        {isCurrent && <circle cx={pos.x} cy={pos.y} r={r + 8} fill="none" stroke="rgba(0,255,136,0.12)" strokeWidth={4} />}
                        {/* Hover ring */}
                        <circle className="sel-ring" cx={pos.x} cy={pos.y} r={r + 6} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={1} opacity="0" />
                        {/* Outer ring decorative */}
                        <circle cx={pos.x} cy={pos.y} r={r + 2} fill="none" stroke={isCurrent ? 'rgba(0,255,136,0.3)' : 'rgba(255,255,255,0.08)'} strokeWidth={1} />
                        {/* Main node body */}
                        <circle cx={pos.x} cy={pos.y} r={r} fill={isCurrent ? '#0d1f14' : '#111111'} stroke={strokeColor} strokeWidth={isCurrent ? 1.5 : 0.8} onDoubleClick={() => { onJump(b.id); }} />
                        {/* Inner accent circle */}
                        <circle cx={pos.x} cy={pos.y} r={r * 0.45} fill="none" stroke={isCurrent ? 'rgba(0,255,136,0.15)' : 'rgba(255,255,255,0.03)'} strokeWidth={1} />
                        {/* Alice depth rings - more rings = more alice activity */}
                        {(() => {
                          const aliceDepth = ((b.aliceConversation || []).filter(m => m.role === 'alice').length) + ((b.aliceFocus?.layers?.length) || 0);
                          if (aliceDepth === 0) return null;
                          const rings = Math.min(aliceDepth, 4);
                          const maxRings = Math.min(aliceDepth, 5);
                          return Array.from({ length: maxRings }).map((_, ri) => (
                            <circle key={ri} cx={pos.x} cy={pos.y} r={r + 10 + ri * 9}
                              fill={ri === 0 ? 'rgba(200,0,0,0.06)' : 'none'}
                              stroke={`rgba(220,${30 - ri * 5},${30 - ri * 5},${1 - ri * 0.12})`}
                              strokeWidth={1.5 - ri * 0.2}
                              opacity={1}
                            />
                          ));
                        })()}
                        {/* Label */}
                        <text x={pos.x} y={line2 ? pos.y - 5 : pos.y + 4} textAnchor="middle" className="node-label" fill={isCurrent ? '#00ff88' : isOnPath ? '#cccccc' : '#888888'} fontSize={isCurrent ? 13 : 11} fontWeight={500} fontFamily="Inter,sans-serif" letterSpacing="0.5" onDoubleClick={() => { onJump(b.id); }}>{line1}</text>
                        {line2 && <text x={pos.x} y={pos.y + 14} textAnchor="middle" className="node-label" fill={isCurrent ? '#00ff88' : isOnPath ? '#cccccc' : '#888888'} fontSize={isCurrent ? 13 : 11} fontWeight={500} fontFamily="Inter,sans-serif" letterSpacing="0.5" onDoubleClick={() => { onJump(b.id); }}>{line2}</text>}
                        {/* Delete button */}
                        <g className="del-btn" opacity="0" onClick={e => { e.stopPropagation(); setConfirmDeleteId(b.id); }}>
                          <circle cx={pos.x + r - 4} cy={pos.y + r - 4} r={9} fill="#0a0a0a" stroke="#2a2a2a" strokeWidth={1} />
                          <text x={pos.x + r - 4} y={pos.y + r} textAnchor="middle" fill="#555555" fontSize={10} fontFamily="Inter,sans-serif">✕</text>
                        </g>

                      </g>
                    );
                  })}
                  {/* Tooltip rendered last so it paints on top of all nodes */}
                  {selectedNode && (() => {
                    const nb = burrows.find(b => b.id === selectedNode);
                    if (!nb) return null;
                    const pos = positions[nb.id];
                    if (!pos) return null;
                    const nr = NODE_R + (nb.id === currentId ? 6 : 0);
                    return (
                      <foreignObject x={pos.x - 130} y={pos.y + nr + 12} width={260} height={160} style={{ pointerEvents: 'none' }}>
                        <div xmlns="http://www.w3.org/1999/xhtml" style={{ background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 10, padding: '10px 12px', fontFamily: 'Inter, sans-serif', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                          {nb.body ? <div style={{ fontSize: 11, color: '#666666', lineHeight: 1.6, marginBottom: 6 }}>{(() => { const clean = nb.body.replace(/\[\[([^\]|]+)(?:\|[^\]]+)?\]\]/g, '$1'); const sentences = clean.match(/[^.!?]+[.!?]+/g) || [clean]; return sentences.slice(0, 2).join(' ').trim(); })()}</div> : <div style={{ fontSize: 11, color: '#444444', fontStyle: 'italic' }}>No content yet</div>}
                          <div style={{ fontSize: 9, color: '#444444', letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 4 }}>double-click to enter</div>
                        </div>
                      </foreignObject>
                    );
                  })()}
                </svg>
                <div style={WP.hint}>Scroll to zoom · Drag to pan · Double-click node to enter</div>
              </div>
            )}
            {confirmDeleteId && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 8 }}>
                <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 14, padding: '28px 32px', textAlign: 'center', maxWidth: 280 }}>
                  <div style={{ fontSize: 15, color: '#ffffff', marginBottom: 8, fontFamily: 'Inter, sans-serif' }}>Delete this burrow?</div>
                  <div style={{ fontSize: 12, color: '#555555', marginBottom: 24, fontFamily: 'Inter, sans-serif' }}>This cannot be undone.</div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button onClick={() => setConfirmDeleteId(null)}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}
                      style={{ fontSize: 13, padding: '8px 20px', borderRadius: 8, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s' }}>Cancel</button>
                    <button onClick={() => { onDeleteBurrow(confirmDeleteId); setConfirmDeleteId(null); }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff5555'; e.currentTarget.style.color = '#ff5555'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#ff3333'; e.currentTarget.style.color = '#ff3333'; }}
                      style={{ fontSize: 13, padding: '8px 20px', borderRadius: 8, border: '1px solid #ff3333', background: 'transparent', color: '#ff3333', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s' }}>Delete</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}



      </div>
    </div>
  );
}

const WP = {
  overlay: { position: 'fixed', inset: 0, zIndex: 200, background: '#080808', display: 'flex', flexDirection: 'column', animation: 'fadeIn 0.25s ease' },
  sheet: { width: '100%', height: '100%', background: 'transparent', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: '1px solid #0f0f0f' },
  headerTitle: { fontSize: 11, fontWeight: 400, color: '#333333', fontFamily: 'Inter, sans-serif', letterSpacing: '3px', textTransform: 'uppercase' },
  closeBtn: { fontSize: 11, background: 'none', border: '1px solid #1a1a1a', borderRadius: 20, color: '#333333', cursor: 'pointer', padding: '6px 16px', fontFamily: 'Inter, sans-serif', letterSpacing: '1px', textTransform: 'uppercase', transition: 'all 0.2s' },
  tabs: { display: 'flex', gap: 4, padding: '16px 28px 0' },
  tabBtn: { fontSize: 11, padding: '6px 16px', borderRadius: 20, border: '1px solid #141414', background: 'transparent', color: '#333333', cursor: 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.15s', letterSpacing: '1px', textTransform: 'uppercase' },
  tabActive: { background: 'transparent', color: '#ffffff', borderColor: '#00b359' },
  tabBadge: { fontSize: 10, background: '#111111', color: '#333333', padding: '1px 6px', borderRadius: 10 },
  tabDivider: { width: 1, height: 16, background: '#1a1a1a', marginLeft: 8, marginRight: 8, flexShrink: 0 },
  menuLabel: { fontSize: 10, letterSpacing: 1.5, color: '#333333', marginRight: 6, whiteSpace: 'nowrap', alignSelf: 'center' },
  legend: { display: 'flex', gap: 20, padding: '12px 28px 0', flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#333333' },
  mapWrap: { flex: 1, overflow: 'hidden', position: 'relative', marginTop: 12, background: 'radial-gradient(circle at 50% 50%, rgba(0,255,136,0.025) 0%, transparent 60%)', userSelect: 'none', touchAction: 'none' },
  hint: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: '#222222', pointerEvents: 'none', whiteSpace: 'nowrap', letterSpacing: '2px', textTransform: 'uppercase' },
  mapSubtitle: { fontSize: 11, color: '#333333', padding: '10px 28px 0', letterSpacing: '0.5px' },
  list: { flex: 1, overflowY: 'auto', padding: '12px 20px 24px' },
  item: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px', borderRadius: 12, border: '1px solid #141414', marginBottom: 8, gap: 16, cursor: 'pointer', transition: 'border-color 0.15s, opacity 0.2s' },
  itemLeft: { flex: 1, minWidth: 0 },
  itemTitle: { fontSize: 14, fontWeight: 400, color: '#888888', marginBottom: 4 },
  itemMeta: { fontSize: 11, color: '#333333', marginBottom: 8 },
  itemBurrows: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  burrowPill: { fontSize: 10, color: '#00b359', background: 'rgba(0,179,89,0.05)', border: '1px solid rgba(0,179,89,0.15)', padding: '2px 8px', borderRadius: 20 },
  burrowMore: { fontSize: 10, color: '#333333', padding: '2px 8px' },
  switchBtn: { fontSize: 11, color: '#333333', whiteSpace: 'nowrap', alignSelf: 'center' },
  itemActions: { display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 },
  restoreBtn: { fontSize: 11, padding: '5px 12px', borderRadius: 8, border: '1px solid #1a1a1a', background: 'transparent', color: '#555555', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  deleteBtn: { fontSize: 11, padding: '5px 12px', borderRadius: 8, border: '1px solid #1a1a1a', background: 'transparent', color: '#333333', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  empty: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#333333', fontStyle: 'italic', padding: '48px 28px', textAlign: 'center' },
};


// ── Mini Warren Map ────────────────────────────────────────────────────────────
function MiniWarren({ activeHole, onOpenWarren }) {
  const { burrows, currentId } = activeHole;
  const W = 130, H = 130, CX = 65, CY = 65, R_STEP = 28, NODE_R = 7;
  const byId = {};
  burrows.forEach(b => { byId[b.id] = { ...b, children: [] }; });
  const roots = [];
  burrows.forEach(b => { if (b.parentId && byId[b.parentId]) byId[b.parentId].children.push(byId[b.id]); else roots.push(byId[b.id]); });
  function countLeaves(n) { return n.children.length === 0 ? 1 : n.children.reduce((s,c) => s + countLeaves(c), 0); }
  const positions = {};
  function layout(node, depth, a0, a1) {
    const angle = (a0 + a1) / 2;
    positions[node.id] = { x: CX + (depth === 0 ? 0 : depth * R_STEP) * Math.cos(angle), y: CY + (depth === 0 ? 0 : depth * R_STEP) * Math.sin(angle) };
    let ca = a0;
    node.children.forEach(child => { const span = (a1-a0)*(countLeaves(child)/Math.max(countLeaves(node),1)); layout(child, depth+1, ca, ca+span); ca+=span; });
  }
  if (roots.length === 1) layout(roots[0], 0, -Math.PI, Math.PI);
  else { let a=-Math.PI; const total=roots.reduce((s,r)=>s+countLeaves(r),0); roots.forEach(r=>{const span=2*Math.PI*(countLeaves(r)/total); layout(r,0,a,a+span); a+=span;}); }
  const pathIds = new Set(); let cur = byId[currentId]; while(cur){pathIds.add(cur.id); cur=cur.parentId?byId[cur.parentId]:null;}
  const edges = burrows.filter(b=>b.parentId&&positions[b.parentId]&&positions[b.id]).map(b=>({x1:positions[b.parentId].x,y1:positions[b.parentId].y,x2:positions[b.id].x,y2:positions[b.id].y,onPath:pathIds.has(b.id)&&pathIds.has(b.parentId)}));
  return (
    <div onClick={onOpenWarren} title="Open Warren" style={{position:'fixed',bottom:96,right:20,zIndex:90,width:W,height:H,cursor:'pointer',background:'rgba(4,4,4,0.88)',borderRadius:12,border:'1px solid #1a1a1a',backdropFilter:'blur(12px)',boxShadow:'0 0 20px rgba(0,0,0,0.6)',transition:'all 0.2s'}}
      onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(0,255,136,0.25)';e.currentTarget.style.boxShadow='0 0 28px rgba(0,255,136,0.07)';}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor='#1a1a1a';e.currentTarget.style.boxShadow='0 0 20px rgba(0,0,0,0.6)';}}>
      <svg width={W} height={H} style={{display:'block',overflow:'visible'}}>
        <circle cx={CX} cy={CY} r={R_STEP} fill="none" stroke="rgba(255,255,255,0.02)" strokeWidth={1}/>
        <circle cx={CX} cy={CY} r={R_STEP*2} fill="none" stroke="rgba(255,255,255,0.015)" strokeWidth={1}/>
        {edges.map((e,i)=><line key={i} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} stroke={e.onPath?'rgba(0,255,136,0.5)':'rgba(255,255,255,0.07)'} strokeWidth={e.onPath?1:0.5}/>)}
        {burrows.map(b=>{
          const pos=positions[b.id]; if(!pos) return null;
          const isCurrent=b.id===currentId; const isOnPath=pathIds.has(b.id);
          const hasAlice=(b.aliceConversation||[]).filter(m=>m.role==='alice').length>0;
          const r=NODE_R+(isCurrent?3:0);
          return <g key={b.id}>
            {isCurrent&&<circle cx={pos.x} cy={pos.y} r={r+5} fill="none" stroke="rgba(0,255,136,0.08)" strokeWidth={5}/>}
            <circle cx={pos.x} cy={pos.y} r={r} fill={isCurrent?'#0d1f14':'#0a0a0a'} stroke={isCurrent?'#00ff88':isOnPath?'rgba(0,255,136,0.3)':'#252525'} strokeWidth={isCurrent?1.5:0.8}/>
            {hasAlice&&<circle cx={pos.x+r-1} cy={pos.y-r+1} r={2} fill="#cc2222"/>}
          </g>;
        })}
      </svg>
      <div style={{position:'absolute',bottom:5,left:0,right:0,textAlign:'center',fontSize:7,color:'#222',letterSpacing:2,textTransform:'uppercase',fontFamily:'Inter,sans-serif'}}>warren</div>
    </div>
  );
}

// ── Home Screen ───────────────────────────────────────────────────────────────
function HomeScreen({ onAsk, allBurrows }) {
  const [showAlice, setShowAlice] = React.useState(false);
  const suggestions = React.useMemo(() => getSuggestions(allBurrows), []);
  return (
    <div style={{ ...HS.root, background: showAlice ? 'rgba(40,0,0,0.4)' : 'transparent' }}>
      {/* Subtle grid background */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 50% 40%, rgba(0,255,136,0.03) 0%, transparent 60%)', pointerEvents: 'none' }} />
      <div style={HS.inner}>
        {showAlice ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              <h1 style={{ ...HS.logo, color: '#ff4444', letterSpacing: '-1px', textShadow: '0 0 40px rgba(255,50,50,0.3)' }}>Alice <span style={{ fontSize: 28, opacity: 0.6 }}>◆</span></h1>
            </div>
            <p style={{ fontSize: 13, color: '#884444', fontFamily: 'Inter, sans-serif', margin: 0, textAlign: 'center', maxWidth: 380, lineHeight: 1.9, letterSpacing: '0.2px' }}>
              Alice lives at the bottom of every burrow. Once you've explored a topic, toggle Alice to ask direct questions and uncover more.
            </p>
            <button onClick={() => setShowAlice(false)}
              style={{ fontSize: 12, padding: '7px 20px', borderRadius: 20, border: '1px solid #333333', background: 'transparent', color: '#555555', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', letterSpacing: '1px', textTransform: 'uppercase' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#00ff88'; e.currentTarget.style.color = '#00ff88'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#333333'; e.currentTarget.style.color = '#555555'; }}
            >Back</button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <h1 style={HS.logo}>Rabbit<span style={HS.dim}>Hole</span></h1>
              <p style={{ fontSize: 11, color: '#333333', fontFamily: 'Inter, sans-serif', letterSpacing: '3px', textTransform: 'uppercase', margin: 0 }}>Follow connections. See how it all relates.</p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 520 }}>
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => onAsk(s)}
                  style={{ fontSize: 12, padding: '8px 16px', borderRadius: 20, border: '1px solid #252525', background: 'rgba(255,255,255,0.02)', color: '#555555', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', letterSpacing: '0.2px' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#00ff88'; e.currentTarget.style.color = '#00ff88'; e.currentTarget.style.background = 'rgba(0,255,136,0.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#252525'; e.currentTarget.style.color = '#555555'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                >{s}</button>
              ))}
            </div>
            <button onClick={() => setShowAlice(true)}
              style={{ fontSize: 11, padding: '6px 16px', borderRadius: 20, border: '1px solid #331111', background: 'transparent', color: '#441111', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', letterSpacing: '1.5px', textTransform: 'uppercase' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#cc2222'; e.currentTarget.style.color = '#cc2222'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#331111'; e.currentTarget.style.color = '#441111'; }}
            >What is Alice?</button>
          </>
        )}
      </div>
    </div>
  );
}

const HS = {
  root: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '60px 40px 0', animation: 'fadeUp 0.5s ease', position: 'relative', overflow: 'hidden' },
  inner: { maxWidth: 580, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32, position: 'relative', zIndex: 1 },
  logo: { fontSize: 62, fontWeight: 400, fontFamily: "'Playfair Display', serif", letterSpacing: '-3px', color: '#ffffff', margin: 0, textShadow: '0 0 60px rgba(0,255,136,0.08)' },
  dim: { color: '#00b359' },
  where: { fontSize: 16, color: '#555555', letterSpacing: '0.3px', fontWeight: 300, margin: 0 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' },
  chip: { fontSize: 13, padding: '8px 18px', borderRadius: 20, border: '1px solid #252525', background: 'rgba(255,255,255,0.02)', color: '#555555', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' },
};

// ── Article ───────────────────────────────────────────────────────────────────
function ArticlePage({ burrow, onLink, streaming, isDigging, liveText, visitedTopics, aliceVisited, onDigDeeper, aliceMode, onToggleAlice, onAliceAsk, aliceFocus, onAliceDigDeeper, suggestions, onSuggest }) {
  const aliceStreamRef = useRef(null);

  const aliceConvLength = (burrow.aliceConversation || []).length;
  useEffect(() => {
    if (isDigging && aliceStreamRef.current) {
      aliceStreamRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isDigging, aliceConvLength]);

  const sources = burrow.sources || [];

  if (streaming && !isDigging) {
    return (
      <div style={AP.root}>
        {!aliceMode && <h1 style={AP.title}>"{burrow.topic}"</h1>}
        {aliceMode && <div style={{ ...AP.body, color: '#666666', fontStyle: 'italic', marginBottom: 12 }}>{burrow.topic}</div>}
        <p id="stream-body" style={{ ...AP.body, color: '#ffffff', whiteSpace: 'pre-wrap', minHeight: 40 }} />
      </div>
    );
  }
  // Split body into sections (each dig deeper adds a new section)
  const sections = (burrow.body || '').split(/\n\n+/);
  const firstSection = sections[0] || '';
  const deeperSections = sections.slice(1);

  const renderParts = (text) => {
    const p = parseBody(text);
    return p.map((part, i) =>
      part.type === 'text'
        ? <span key={i}>{part.value}</span>
        : (() => {
            const visited = aliceMode ? (aliceVisited || new Set()).has(part.topic.toLowerCase().trim()) : visitedTopics.has(part.topic.toLowerCase().trim());
            const baseColor = aliceMode ? (visited ? '#dd6666' : '#ff4444') : (visited ? '#00b359' : '#00ff88');
            const hoverColor = aliceMode ? '#ff6666' : '#00b359';
            return (
              <span key={i} style={{ ...( visited ? AP.linkVisited : AP.link ), color: baseColor }}
                onClick={() => aliceMode ? onAliceAsk(`__ALICE_FOCUS__${part.topic}`) : onLink(part.topic)}
                onMouseEnter={e => { e.currentTarget.style.color = hoverColor; }}
                onMouseLeave={e => { e.currentTarget.style.color = baseColor; }}
              >{part.label}</span>
            );
          })()
    );
  };

  const aliceAccent = '#ff4444';
  const linkColor = aliceMode ? aliceAccent : '#00ff88';
  const linkVisitedColor = aliceMode ? '#cc2222' : '#00b359';

  return (
    <div style={AP.root}>
      <h1 style={{ ...AP.title, color: aliceMode ? '#ff6666' : '#ffffff' }}>"{burrow.topic}"</h1>

      {/* First section */}
      <p style={AP.body}>{renderParts(firstSection)}</p>

      {/* Sources after first section */}
      {sources.length > 0 && (
        <div style={AP.sources}>
          <div style={{ ...AP.sourcesTitle, color: aliceMode ? '#552222' : '#444444' }}>Sources</div>
          {sources.map((s, i) => {
            const sourceTopic = s.title;
            const visited = visitedTopics.has(sourceTopic.toLowerCase().trim());
            return (
              <div key={i} style={AP.sourceItem}>
                <span
                  style={{ ...AP.sourceLink, color: aliceMode ? (visited ? '#cc2222' : '#ff4444') : (visited ? '#00b359' : '#00ff88') }}
                  onClick={() => onLink(sourceTopic)}
                  onMouseEnter={e => { e.currentTarget.style.color = aliceMode ? '#ff6666' : '#00b359'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = aliceMode ? (visited ? '#cc2222' : '#ff4444') : (visited ? '#00b359' : '#00ff88'); }}
                >
                  {s.title}
                </span>
                <a href={s.url} target="_blank" rel="noopener noreferrer" style={AP.sourceUrl}
                  onClick={e => e.stopPropagation()}
                >↗</a>
              </div>
            );
          })}
        </div>
      )}

      {/* Deeper sections */}
      {deeperSections.map((sec, i) => (
        <div key={i} style={{ marginTop: 32, paddingTop: 32, borderTop: '1px solid #222222' }}>
          <p style={AP.body}>{renderParts(sec)}</p>
        </div>
      ))}

      {/* Streaming new dig deeper content */}
      {isDigging && (
        <div style={{ marginTop: 32, paddingTop: 32, borderTop: '1px solid #222222' }}>
          <p id="stream-body" style={{ ...AP.body, whiteSpace: 'pre-wrap', minHeight: 40 }} />
        </div>
      )}

      {!streaming && !aliceMode && (
        <div style={AP.digWrap}>
          <span style={AP.digBtn}
            onClick={onDigDeeper}
            onMouseEnter={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = '#00b359'; }}
          >dig deeper ↓</span>
        </div>
      )}

      {/* Alice conversation */}
      {(aliceMode || (burrow.aliceConversation && burrow.aliceConversation.length > 0)) && (
        <div style={{ marginTop: 48, borderTop: `1px solid ${aliceMode ? '#2a0000' : '#1a1a1a'}`, paddingTop: 32 }}>

          {(burrow.aliceConversation || []).map((msg, i) => (
            <div key={i} style={{ marginBottom: 24 }}>
              {msg.role === 'user' ? (
                !msg.isLink && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span style={{ fontSize: 9, color: '#441111', flexShrink: 0 }}>◇</span>
                    <p style={{ ...AP.body, color: '#553333', fontStyle: 'italic', margin: 0, fontSize: 15 }}>{msg.text}</p>
                  </div>
                )
              ) : (
                <div style={{ paddingLeft: 20, borderLeft: `1px solid ${aliceMode ? '#3a1111' : '#1f1f1f'}` }}>
                  {msg.focusTopic && <div style={{ fontSize: 9, letterSpacing: 2.5, color: '#661111', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', marginBottom: 10 }}>◆ {msg.focusTopic}{msg.isDeeper ? ' +' : ''}</div>}
                  <p style={{ ...AP.body, marginTop: 0, marginBottom: 8, color: '#dddddd' }}>{renderParts(msg.text)}</p>
                </div>
              )}
            </div>
          ))}

          {/* Streaming — always at very bottom */}
          {isDigging && (
            <div ref={aliceStreamRef} style={{ paddingLeft: 20, borderLeft: '1px solid #3a1111', marginBottom: 24 }}>
              <p id="alice-stream-body" style={{ ...AP.body, whiteSpace: 'pre-wrap', minHeight: 40, marginTop: 0, color: '#dddddd' }} />
            </div>
          )}

          {/* Dig deeper */}
          {aliceMode && !isDigging && !streaming && burrow.aliceLastFocus && (
            <div style={{ marginBottom: 24 }}>
              <button style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#882222', border: '1px solid #3a1111', borderRadius: 20, padding: '8px 24px', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', background: 'transparent' }}
                onClick={(e) => { e.stopPropagation(); onAliceDigDeeper(); }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#cc2222'; e.currentTarget.style.color = '#cc2222'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#3a1111'; e.currentTarget.style.color = '#882222'; }}
              >◆ dig deeper</button>
            </div>
          )}

          {/* Input */}
          {aliceMode && !isDigging && !streaming && (
            <div style={{ display: 'flex', gap: 0, marginTop: 8 }}>
              <input id="alice-input" placeholder="Ask Alice..."
                style={{ flex: 1, background: '#0a0000', border: '1px solid #2a1111', borderRight: 'none', borderRadius: '12px 0 0 12px', padding: '13px 20px', fontSize: 14, color: '#dddddd', outline: 'none', fontFamily: 'Inter, sans-serif' }}
                onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { onAliceAsk(e.target.value.trim()); e.target.value = ''; } }}
              />
              <button
                style={{ padding: '0 20px', background: '#1a0000', border: '1px solid #2a1111', borderRadius: '0 12px 12px 0', color: '#cc2222', cursor: 'pointer', fontSize: 16, transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#cc2222'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#1a0000'; e.currentTarget.style.color = '#cc2222'; }}
                onClick={() => { const el = document.getElementById('alice-input'); if (el && el.value.trim()) { onAliceAsk(el.value.trim()); el.value = ''; } }}
              >◆</button>
            </div>
          )}
        </div>
      )}

      {/* Suggested questions */}
      {!aliceMode && !streaming && suggestions && suggestions.length > 0 && (
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid #111111' }}>
          <div style={{ fontSize: 9, letterSpacing: 3, color: '#222222', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', marginBottom: 16 }}>Where to next</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {suggestions.map((s, i) => (
              <div key={i} onClick={() => onSuggest(s)}
                style={{ fontSize: 14, color: '#333333', cursor: 'pointer', fontFamily: 'Inter, sans-serif', padding: '10px 0', borderBottom: '1px solid #0f0f0f', transition: 'color 0.2s', letterSpacing: '0.01em' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#00ff88'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#333333'; }}
              >→ {s}</div>
            ))}
          </div>
        </div>
      )}

            {/* Alice toggle at bottom */}
      {!aliceMode && !streaming && (
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid #1a1a1a', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <p style={{ fontSize: 13, color: '#cc2222', fontFamily: 'Inter, sans-serif', margin: 0, letterSpacing: '0.3px' }}>Have questions? Narrow in.</p>
          <button onClick={onToggleAlice}
            style={{ fontSize: 12, padding: '7px 20px', borderRadius: 20, border: '1px solid #cc2222', background: 'transparent', color: '#cc2222', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', letterSpacing: '0.5px' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff4444'; e.currentTarget.style.color = '#ff4444'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#cc2222'; e.currentTarget.style.color = '#cc2222'; }}
          >◇ Alice</button>
        </div>
      )}
      {aliceMode && !streaming && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <button onClick={onToggleAlice}
            style={{ fontSize: 11, padding: '5px 16px', borderRadius: 20, border: '1px solid #00b359', background: 'transparent', color: '#00b359', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#00b359'; e.currentTarget.style.color = '#00b359'; }}
          >back to RabbitHole</button>
        </div>
      )}
    </div>
  );
}

const AP = {
  root: { padding: '64px 56px 120px', maxWidth: 720, margin: '0 auto', animation: 'fadeUp 0.4s ease', userSelect: 'text', WebkitUserSelect: 'text' },
  burrowTag: { fontSize: 10, fontWeight: 700, letterSpacing: 3, color: '#333333', marginBottom: 20, fontFamily: 'Inter, sans-serif', textTransform: 'uppercase' },
  title: { fontSize: 34, fontWeight: 400, fontFamily: "'Playfair Display', serif", color: '#f0f0f0', marginBottom: 12, lineHeight: 1.2, letterSpacing: '-0.3px' },
  meta: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 36, fontSize: 12, color: '#444444' },
  label: { color: '#555555' },
  dot: { color: '#333333' },
  domain: { color: '#444444', fontStyle: 'italic' },
  body: { fontSize: 17, lineHeight: 2.1, color: '#e0e0e0', userSelect: 'text', letterSpacing: '0.01em' },
  link: { color: '#00ff88', textDecoration: 'none', cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none', WebkitUserSelect: 'none' },
  linkVisited: { color: '#00b359', textDecoration: 'none', cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none', WebkitUserSelect: 'none', borderBottom: '1px solid rgba(0,179,89,0.2)' },
  cursor: { color: '#444444', animation: 'pulse 1s ease-in-out infinite' },
  sources: { marginTop: 40, paddingTop: 24, borderTop: '1px solid #1f1f1f' },
  sourcesTitle: { fontSize: 10, fontWeight: 700, letterSpacing: 3, color: '#333333', marginBottom: 14, fontFamily: 'Inter, sans-serif', textTransform: 'uppercase' },
  sourceItem: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  sourceLink: { fontSize: 13, cursor: 'pointer', transition: 'color 0.1s', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sourceUrl: { fontSize: 12, color: '#333333', textDecoration: 'none', flexShrink: 0 },
  digWrap: { marginTop: 60, marginBottom: 32, display: 'flex', justifyContent: 'center' },
  digBtn: { fontSize: 12, color: '#888888', border: '1px solid #2a2a2a', borderRadius: 20, padding: '10px 36px', cursor: 'pointer', transition: 'all 0.2s', letterSpacing: '1.5px', userSelect: 'none', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', background: 'transparent' },
};

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    try { const u = localStorage.getItem('rh_user'); return u ? JSON.parse(u) : null; } catch { return null; }
  });
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const savePromptShown = useRef(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const login = async (e) => {
    e && e.preventDefault();
    setAuthLoading(true); setAuthError('');
    try {
      const endpoint = authMode === 'login' ? '/api/login' : '/api/signup';
      const res = await fetch('https://rabbithole-api.onrender.com' + endpoint, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: authEmail, password: authPassword })
      });
      const data = await res.json();
      if (!res.ok) { setAuthError(data.error || 'Something went wrong'); setAuthLoading(false); return; }
      localStorage.setItem('rh_token', data.token);
      localStorage.setItem('rh_user', JSON.stringify(data.user));
      setUser(data.user);
      setShowAuth(false);
      setAuthEmail(''); setAuthPassword('');
    } catch { setAuthError('Connection failed'); }
    setAuthLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('rh_token');
    localStorage.removeItem('rh_user');
    setUser(null);
  };

  // When Stripe redirects back with ?upgraded=true, refresh user from DB
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === 'true') {
      const token = localStorage.getItem('rh_token');
      if (token) {
        fetch('https://rabbithole-api.onrender.com/api/me', {
          headers: { 'authorization': 'Bearer ' + token }
        })
          .then(r => r.json())
          .then(freshUser => {
            if (freshUser && freshUser.tier) {
              localStorage.setItem('rh_user', JSON.stringify(freshUser));
              setUser(freshUser);
            }
          })
          .catch(() => {});
      }
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const upgradeToMeridian = async () => {
    if (!user) { setShowAuth(true); setAuthMode('signup'); setShowMenu(false); return; }
    setShowMenu(false);
    try {
      const token = localStorage.getItem('rh_token');
      const res = await fetch('https://rabbithole-api.onrender.com/api/create-checkout', {
        method: 'POST', headers: { 'content-type': 'application/json', 'authorization': 'Bearer ' + token }
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert('Could not start checkout — ' + (data.error || 'unknown error'));
    } catch (e) { alert('Could not start checkout: ' + e.message); }
  };

  const [holes, setHoles] = useState(() => {
    const saved = loadState();
    // Always start on the home screen, not mid-burrow
    if (saved && saved.length) return saved.map(h => ({ ...h, currentId: null }));
    return [newHole()];
  });
  const [activeId, setActiveId] = useState(() => {
    const saved = loadState();
    return saved?.[0]?.id || holes[0]?.id;
  });
  const [archive, setArchive] = useState(() => loadArchive());
  const [showWarren, setShowWarren] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [isDigging, setIsDigging] = useState(false);
  const [aliceMode, setAliceMode] = useState(false);
  const [aliceVisited, setAliceVisited] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [aliceSuggestions, setAliceSuggestions] = useState([]);
  const [liveText, setLiveText] = useState('');
  const [error, setError] = useState('');
  const [input, setInput] = useState('');
  const liveRef = useRef('');

  useEffect(() => { saveState(holes); }, [holes]);

  const activeHole = holes.find(h => h.id === activeId) || holes[0];
  const currentBurrow = activeHole?.burrows.find(b => b.id === activeHole.currentId) || null;
  const allBurrows = useMemo(() => holes.flatMap(h => h.burrows), [holes]);
  const visitedTopics = useMemo(() => new Set(activeHole.burrows.map(b => b.topic.toLowerCase().trim())), [activeHole.burrows]);
  const canGoBack = !!(activeHole?.currentId);

  const navigate = useCallback(async (topic) => {
    if (streaming) return;
    setError('');

    const cacheKey = topic.toLowerCase().trim();
    const existing = activeHole.burrows.find(b => b.topic.toLowerCase() === cacheKey);
    if (existing) {
      setHoles(hs => hs.map(h => h.id === activeId ? { ...h, currentId: existing.id } : h));
      return;
    }

    const trail = buildTrail(activeHole.burrows, activeHole.currentId);
    const burrowId = uid();
    // If on home screen but hole has burrows, attach to first root burrow to avoid multiple roots
    const parentId = activeHole.currentId || (activeHole.burrows.length > 0 ? activeHole.burrows[0].id : null);
    const burrow = { id: burrowId, topic, body: '', label: null, domain: null, parentId };

    setHoles(hs => hs.map(h => {
      if (h.id !== activeId) return h;
      const newBurrows = [...h.burrows, burrow];
      const title = newBurrows[0]?.topic || 'New Rabbit Hole';
      if (newBurrows.length === 3 && !user && !savePromptShown.current) { savePromptShown.current = true; setTimeout(() => setShowSavePrompt(true), 1200); }
      return { ...h, burrows: newBurrows, currentId: burrowId };
    }));

    liveRef.current = '';
    setStreaming(true);

    streamAsk(
      topic,
      trail,
      (fullText) => {
        // Write directly to DOM element — no React state update
        liveRef.current = fullText;
        const el = document.getElementById('stream-body');
        if (el) el.textContent = fullText + ' ▍';
        // Don't update React state — DOM write is enough
      },
      (finalText, sources) => {
        // Clear DOM stream element immediately to prevent flash of duplicate
        const el = document.getElementById('stream-body');
        if (el) el.textContent = '';
        // Stream done — commit to holes state once
        const body = finalText || liveRef.current;
        setHoles(hs => hs.map(h => ({
          ...h,
          burrows: h.burrows.map(b => b.id === burrowId ? { ...b, body, sources: sources || [] } : b),
        })));
        setStreaming(false);
        liveRef.current = '';

        apiFetch('/api/label', { topic }).then(({ summary, domain }) => {
          setHoles(hs => hs.map(h => ({
            ...h,
            burrows: h.burrows.map(b => b.id === burrowId ? { ...b, label: summary, domain } : b),
          })));
        }).catch(() => {});
      },
      () => {
        setStreaming(false);
        liveRef.current = '';
        setError('Could not connect. Make sure the server is running and your .env file has a valid ANTHROPIC_API_KEY.');
      }
    );
  }, [activeHole, activeId, streaming]);

  const aliceAsk = useCallback(async (question) => {
    if (streaming || !currentBurrow) return;
    setError('');
    const burrowId = currentBurrow.id;

    // Identity intercepts
    const q = question.toLowerCase().trim();
    const isIdentity = q.includes('what are you') || q.includes('who are you') || q.includes('who made you') || q.includes('what is alice') || q.includes('are you ai') || q.includes('are you claude') || q.includes('are you chatgpt') || q.includes('who built you') || q.includes('what model');
    const isCompany = q.includes('what company') || q.includes('which company') || q.includes('who owns') || q.includes('anthropic') || q.includes('openai') || q.includes('who is behind') || q.includes('what organization') || q.includes('who created');
    if (isIdentity || isCompany) {
      const answer = isCompany ? "I am the one who followed the white rabbit." : "The Rabbit Hole told me not to answer that.";
      setHoles(hs => hs.map(h => ({ ...h, burrows: h.burrows.map(b => b.id === burrowId ? { ...b, aliceConversation: [...(b.aliceConversation || []), { role: 'user', text: question }, { role: 'alice', text: answer }] } : b) })));
      return;
    }

    const isLinkClick = question.startsWith('__ALICE_FOCUS__');
    const focusTopic = isLinkClick ? question.slice('__ALICE_FOCUS__'.length) : null;
    const displayQuestion = isLinkClick ? focusTopic : question;

    // Get current conversation snapshot
    const currentConv = currentBurrow.aliceConversation || [];

    // Add user turn to conversation (link clicks show as the topic, typed questions as-is)
    const userMsg = { role: 'user', text: displayQuestion, isLink: isLinkClick };
    setHoles(hs => hs.map(h => ({ ...h, burrows: h.burrows.map(b => b.id === burrowId ? { ...b, aliceConversation: [...(b.aliceConversation || []), userMsg] } : b) })));

    // Build context
    const burrowContext = `Topic: ${currentBurrow.topic}\n\nContent: ${(currentBurrow.body || '').slice(0, 400)}`;
    const historyText = currentConv.map(m => (m.role === 'user' ? 'User' : 'Alice') + ': ' + m.text).join('\n');
    const serverQ = `__ALICE_CONTEXT__${burrowContext}${historyText ? '\n\n' + historyText : ''}\n\nUser: ${isLinkClick ? `Tell me about ${focusTopic} in 1-2 sentences` : question}`;

    if (isLinkClick) setAliceVisited(v => new Set([...v, focusTopic.toLowerCase().trim()]));

    liveRef.current = '';
    setIsDigging(true);
    setStreaming(true);
    const trail = buildTrail(activeHole.burrows, activeHole.currentId);

    streamAsk(serverQ, trail,
      (fullText) => { liveRef.current = fullText; const el = document.getElementById('alice-stream-body'); if (el) el.textContent = fullText + ' ▍'; },
      (finalText) => {
        const el = document.getElementById('alice-stream-body'); if (el) el.textContent = '';
        const aliceMsg = { role: 'alice', text: finalText, focusTopic: isLinkClick ? focusTopic : null };
        setHoles(hs => hs.map(h => ({ ...h, burrows: h.burrows.map(b => b.id === burrowId ? { ...b, aliceConversation: [...(b.aliceConversation || []), aliceMsg], aliceLastFocus: isLinkClick ? focusTopic : b.aliceLastFocus } : b) })));
        setStreaming(false); setIsDigging(false); liveRef.current = '';
        // Generate alice suggestions after typed questions
        if (!isLinkClick) {
          setTimeout(async () => {
            try {
              const r = await fetch('https://rabbithole-api.onrender.com/api/suggest', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ topic: displayQuestion, body: finalText })
              });
              if (r.ok) { const d = await r.json(); setAliceSuggestions(d.suggestions || []); }
            } catch {}
          }, 300);
        }
      },
      () => { setStreaming(false); setIsDigging(false); liveRef.current = ''; },
      true
    );
  }, [currentBurrow, activeHole, activeId, streaming]);

  const aliceDigDeeper = useCallback(async () => {
    if (streaming || !currentBurrow) return;
    const burrowId = currentBurrow.id;
    const lastFocus = currentBurrow.aliceLastFocus;
    if (!lastFocus) return;

    const conv = currentBurrow.aliceConversation || [];
    const focusAnswers = conv.filter(m => m.role === 'alice' && m.focusTopic === lastFocus).map(m => m.text);
    const depthMap = [2, 3, 4, 5];
    const sentences = depthMap[Math.min(focusAnswers.length, 3)];
    const covered = focusAnswers.join(' ');

    const q = `__ALICE_DEEPER__${lastFocus}||${covered}||${sentences}`;
    liveRef.current = '';
    setIsDigging(true);
    setStreaming(true);
    const trail = buildTrail(activeHole.burrows, activeHole.currentId);

    setTimeout(() => {
      streamAsk(q, trail,
        (fullText) => { liveRef.current = fullText; const el = document.getElementById('alice-stream-body'); if (el) el.textContent = fullText + ' ▍'; },
        (finalText) => {
          const el = document.getElementById('alice-stream-body'); if (el) el.textContent = '';
          const aliceMsg = { role: 'alice', text: finalText, focusTopic: lastFocus, isDeeper: true };
          setHoles(hs => hs.map(h => ({ ...h, burrows: h.burrows.map(b => b.id === burrowId ? { ...b, aliceConversation: [...(b.aliceConversation || []), aliceMsg] } : b) })));
          setStreaming(false); setIsDigging(false); liveRef.current = '';
        },
        () => { setStreaming(false); setIsDigging(false); liveRef.current = ''; },
        true
      );
    }, 50);
  }, [currentBurrow, activeHole, activeId, streaming]);

  const [aliceFocus, setAliceFocus] = useState(null);
  const aliceFocusRef = useRef(null);
  aliceFocusRef.current = aliceFocus;

    const digDeeper = useCallback(async () => {
    if (streaming || !currentBurrow) return;
    setError('');

    const burrowId = currentBurrow.id;
    const existingBody = currentBurrow.body || '';
    const topic = currentBurrow.topic;

    liveRef.current = '';
    setIsDigging(true);
    setStreaming(true);

    // Build a trail that includes the current burrow's full body
    const trail = buildTrail(activeHole.burrows, activeHole.currentId);

    const digPrompt = `__DIG_DEEPER__${existingBody}`;

    streamAsk(
      digPrompt,
      trail,
      (fullText) => {
        liveRef.current = fullText;
        // Append to existing body in DOM
        const el = document.getElementById('stream-body');
        if (el) el.textContent = fullText + ' ▍';
      },
      (finalText, sources) => {
        const el = document.getElementById('stream-body');
        if (el) el.textContent = '';
        // Append new content to existing body
        const newBody = existingBody + '\n\n' + finalText;
        const newSources = sources?.length ? sources : (currentBurrow.sources || []);
        setHoles(hs => hs.map(h => ({
          ...h,
          burrows: h.burrows.map(b => b.id === burrowId ? { ...b, body: newBody, sources: newSources } : b),
        })));
        setStreaming(false);
        setIsDigging(false);
        liveRef.current = '';
        // Generate suggestions from this burrow
        setSuggestions([]);
        setTimeout(async () => {
          try {
            const r = await fetch('https://rabbithole-api.onrender.com/api/suggest', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ prompt: `Based on this text about "${topic}":\n\n${finalText.slice(0, 500)}\n\nGenerate exactly 3 short provocative follow-up questions. Return ONLY a JSON array of 3 strings.` })
            });
            if (r.ok) { const d = await r.json(); setSuggestions(d.questions || []); }
          } catch {}
        }, 500);
      },
      () => {
        setStreaming(false);
        setIsDigging(false);
        liveRef.current = '';
        setError('Could not connect.');
      }
    );
  }, [currentBurrow, activeHole, activeId, streaming]);

  const goBack = useCallback(() => {
    if (!currentBurrow || streaming) return;
    setHoles(hs => hs.map(h => h.id === activeId ? { ...h, currentId: currentBurrow.parentId || null } : h));
  }, [currentBurrow, activeId, streaming]);

  const jumpTo = useCallback((burrowId) => {
    setHoles(hs => hs.map(h => h.id === activeId ? { ...h, currentId: burrowId } : h));
  }, [activeId]);

  const newHoleHandler = () => {
    const h = newHole();
    setHoles(hs => [...hs, h]);
    setActiveId(h.id);
  };

  const deleteBurrow = (burrowId) => {
    setHoles(hs => hs.map(h => {
      if (h.id !== activeId) return h;
      // Collect all descendant ids recursively
      const toDelete = new Set();
      const collect = (id) => {
        toDelete.add(id);
        h.burrows.filter(b => b.parentId === id).forEach(b => collect(b.id));
      };
      collect(burrowId);
      const newBurrows = h.burrows.filter(b => !toDelete.has(b.id));
      const deletedBurrow = h.burrows.find(b => b.id === burrowId);
      const newCurrentId = toDelete.has(h.currentId) ? (deletedBurrow?.parentId || null) : h.currentId;
      return { ...h, burrows: newBurrows, currentId: newCurrentId };
    }));
  };

  const renameHole = (id, newTitle) => {
    const t = newTitle.trim();
    if (!t) return;
    setHoles(hs => hs.map(h => h.id === id ? { ...h, title: t } : h));
  };

  const reorderHoles = (fromIndex, toIndex) => {
    setHoles(hs => {
      const next = [...hs];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const closeHole = (id) => {
    setHoles(hs => {
      const hole = hs.find(h => h.id === id);
      if (hole) {
        const archived = { ...hole, deletedAt: Date.now() };
        setArchive(a => { const next = [...a, archived]; saveArchive(next); return next; });
      }
      const next = hs.filter(h => h.id !== id);
      if (next.length === 0) {
        const fresh = newHole();
        setActiveId(fresh.id);
        return [fresh];
      }
      if (activeId === id) setActiveId(next[next.length - 1]?.id);
      return next;
    });
  };

  const restoreHole = (hole) => {
    const restored = { ...hole, deletedAt: undefined };
    setHoles(hs => [...hs, restored]);
    // Don't switch — just restore quietly to "Your Rabbit Holes"
    setArchive(a => { const next = a.filter(h => h.id !== hole.id); saveArchive(next); return next; });
  };

  const deletePermanent = (id) => {
    setArchive(a => { const next = a.filter(h => h.id !== id); saveArchive(next); return next; });
  };

  const submit = () => {
    const q = input.trim();
    if (q && !streaming) { setInput(''); navigate(q); }
  };

  return (
    <div style={A.root}>
      <header style={A.header}>
        <button style={{ ...A.mapBtn, position: 'fixed', top: 16, left: 20, zIndex: 100 }} onClick={() => setShowWarren(true)}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}>
          ⬡ Map
          {activeHole.burrows.length > 0 && <span style={A.warrenCount}>{activeHole.burrows.length}</span>}
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ position: 'fixed', top: 16, right: 20, zIndex: 100, display: 'flex', gap: 8 }}>
          {user ? (
            <button style={{ ...A.menuBtn, background: user.tier === 'meridian' ? '#0d1f16' : '#1a1a1a', borderColor: user.tier === 'meridian' ? '#00b359' : '#2a2a2a', color: user.tier === 'meridian' ? '#00b359' : '#888888', fontSize: 13 }}
              onClick={() => setShowMenu(m => !m)}>
              {user.tier === 'meridian' ? '✦ Meridian' : user.email.split('@')[0]}
            </button>
          ) : (
            <button style={{ ...A.menuBtn, fontSize: 13 }}
              onClick={() => { setShowAuth(true); setAuthMode('login'); }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}>
              Sign in
            </button>
          )}
          <button style={{ ...A.menuBtn, position: 'relative' }} onClick={() => setShowMenu(m => !m)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}>
            ☰ Menu
          </button>
        </div>
      </header>

      <main style={A.main}>
        {error && <div style={A.error}>{error}</div>}
        {currentBurrow
          ? <ArticlePage burrow={currentBurrow} onLink={navigate} streaming={streaming} isDigging={isDigging} liveText={liveText} visitedTopics={visitedTopics} aliceVisited={aliceVisited} onDigDeeper={digDeeper} aliceMode={aliceMode} onToggleAlice={() => setAliceMode(a => !a)} onAliceAsk={aliceAsk} aliceFocus={aliceFocus} onAliceDigDeeper={aliceDigDeeper} suggestions={suggestions} onSuggest={navigate} />
          : <HomeScreen onAsk={navigate} allBurrows={allBurrows} />
        }
      </main>

      {/* Back (left) + New hole (right) row */}
      <div style={A.mapBar}>
        {canGoBack && !streaming && (() => {
          const byId = {};
          activeHole.burrows.forEach(b => { byId[b.id] = b; });
          const parentBurrow = currentBurrow?.parentId ? byId[currentBurrow.parentId] : null;
          return (
            <button style={{ ...A.backBtn, position: 'fixed', bottom: 90, left: 20, zIndex: 100 }} onClick={goBack}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#aaaaaa'; }}
            >← Back</button>
          );
        })()}
        <div style={{ flex: 1 }} />
      </div>

      {/* Search bar — input and shovel fused */}
      <div style={A.inputBar}>
        <input
          style={{ ...A.input, opacity: streaming ? 0.5 : 1, borderRadius: '12px 0 0 12px', borderRight: 'none' }}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder={streaming ? 'Digging…' : 'Ask anything...'}
          disabled={streaming}
          autoComplete="off"
        />
        <button style={{ ...A.askBtn, opacity: streaming ? 0.4 : 1, borderRadius: '0 12px 12px 0', background: '#161616', border: '1px solid #2a2a2a', borderLeft: 'none' }} onClick={submit} disabled={streaming}>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M17 4l3 3" />
            <path d="M18.5 5.5l-8 8" />
            <path d="M8.276 11.284l4.44 4.44a.968 .968 0 0 1 0 1.369l-2.704 2.704a4.108 4.108 0 0 1 -5.809 -5.81l2.704 -2.703a.968 .968 0 0 1 1.37 0l-.001 0" />
          </svg>
        </button>
      </div>
      {streaming && <div style={A.streamingBadge}>digging…</div>}


      {showSavePrompt && !user && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#050505', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.4s ease' }}
          onClick={() => setShowSavePrompt(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32, maxWidth: 480, padding: '0 40px' }} onClick={e => e.stopPropagation()}>
            {/* Big logo */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 86, fontWeight: 300, fontFamily: "'Playfair Display', serif", letterSpacing: '-5px', color: '#ffffff', lineHeight: 1, textShadow: '0 0 80px rgba(0,255,136,0.1)' }}>
                Rabbit<span style={{ color: '#00ff88' }}>Hole</span>
              </div>
              <div style={{ fontSize: 10, letterSpacing: 5, textTransform: 'uppercase', color: '#222222', marginTop: 8, fontFamily: 'Inter, sans-serif' }}>Your trail is worth saving</div>
            </div>

            {/* Message */}
            <p style={{ fontSize: 16, color: '#555555', textAlign: 'center', lineHeight: 1.8, margin: 0, fontFamily: 'Inter, sans-serif' }}>
              You've opened 3 burrows. Create a free account to save your warren across devices and pick up where you left off.
            </p>

            {/* CTA */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
              <button onClick={() => { setShowSavePrompt(false); setAuthMode('signup'); setShowAuth(true); }}
                style={{ width: '100%', padding: '15px', borderRadius: 12, border: 'none', background: '#00ff88', color: '#080808', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', letterSpacing: '0.3px', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#00cc70'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#00ff88'; }}
              >Save my rabbit hole</button>
              <button onClick={() => { setShowSavePrompt(false); setAuthMode('login'); setShowAuth(true); }}
                style={{ width: '100%', padding: '13px', borderRadius: 12, border: '1px solid #1a1a1a', background: 'transparent', color: '#444444', fontSize: 13, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.color = '#888'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1a1a1a'; e.currentTarget.style.color = '#444444'; }}
              >I already have an account</button>
              <button onClick={() => setShowSavePrompt(false)}
                style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: '#222222', fontSize: 12, cursor: 'pointer', fontFamily: 'Inter, sans-serif', letterSpacing: '0.5px' }}
              >Continue without saving</button>
            </div>
          </div>
        </div>
      )}

      {showAuth && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }} onClick={() => setShowAuth(false)}>
          <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 16, padding: '36px 32px', width: 340, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: '#ffffff', fontFamily: "'Playfair Display', serif", margin: '0 0 6px' }}>
              {authMode === 'login' ? 'Sign in' : 'Create account'}
            </h2>
            <p style={{ fontSize: 13, color: '#555555', margin: '0 0 24px', fontFamily: 'Inter, sans-serif' }}>
              {authMode === 'login' ? 'Welcome back.' : 'Start your rabbit hole journey.'}
            </p>
            {authError && <div style={{ fontSize: 13, color: '#ff5555', marginBottom: 14, fontFamily: 'Inter, sans-serif' }}>{authError}</div>}
            <input
              type="email" placeholder="Email" value={authEmail}
              onChange={e => setAuthEmail(e.target.value)}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #2a2a2a', background: '#111111', color: '#ffffff', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', marginBottom: 10, boxSizing: 'border-box' }}
            />
            <input
              type="password" placeholder="Password" value={authPassword}
              onChange={e => setAuthPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && login()}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #2a2a2a', background: '#111111', color: '#ffffff', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', marginBottom: 16, boxSizing: 'border-box' }}
            />
            <button onClick={login} disabled={authLoading}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#00ff88', color: '#111111', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', marginBottom: 14 }}
            onMouseEnter={e => { if (!authLoading) { e.currentTarget.style.background = '#00cc70'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = '#00ff88'; }}
            >{authLoading ? 'Please wait...' : authMode === 'login' ? 'Sign in' : 'Create account'}</button>
            <p style={{ fontSize: 13, color: '#555555', textAlign: 'center', margin: 0, fontFamily: 'Inter, sans-serif' }}>
              {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <span onClick={() => { setAuthMode(authMode === 'login' ? 'signup' : 'login'); setAuthError(''); }}
                style={{ color: '#00b359', cursor: 'pointer' }}>
                {authMode === 'login' ? 'Sign up' : 'Sign in'}
              </span>
            </p>
          </div>
        </div>
      )}

      {currentBurrow && activeHole.burrows.length >= 2 && <MiniWarren activeHole={activeHole} onOpenWarren={() => setShowWarren(true)} />}
      {showMenu && <MenuPanel holes={holes} activeHole={activeHole} archive={archive} onSwitch={id => setActiveId(id)} onCloseHole={closeHole} onRestore={restoreHole} onDeletePermanent={deletePermanent} onViewWarren={(id) => { setActiveId(id); setShowMenu(false); setTimeout(() => setShowWarren(true), 50); }} onClose={() => setShowMenu(false)} onRename={renameHole} onNewHole={newHoleHandler} user={user} onUpgrade={upgradeToMeridian} onLogout={logout} onLogin={() => { setShowAuth(true); setAuthMode('login'); setShowMenu(false); }} />}
      {showWarren && <WarrenPanel activeHole={activeHole} holes={holes} archive={archive} onJump={jumpTo} onSwitch={id => { setActiveId(id); }} onRestore={restoreHole} onDeletePermanent={deletePermanent} onClose={() => setShowWarren(false)} onViewWarren={(id) => { setActiveId(id); setTimeout(() => setShowWarren(true), 50); }} onRenameHole={renameHole} onCloseHole={closeHole} onDeleteBurrow={deleteBurrow} />}
    </div>
  );
}

const A = {
  root: { display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden', background: '#080808', fontFamily: 'Inter, sans-serif', userSelect: 'none', WebkitUserSelect: 'none' },
  header: { height: 0, flexShrink: 0 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flex: 1, overflow: 'hidden' },
  backBtn: { fontSize: 13, padding: '9px 20px', borderRadius: 20, border: '1px solid #222222', background: 'rgba(0,0,0,0.4)', color: '#666666', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.2s', letterSpacing: '0.5px', backdropFilter: 'blur(10px)' },
  crumb: { fontSize: 12, color: '#444444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  backLabel: { fontSize: 12, color: '#555555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  streamingBadge: { fontSize: 11, color: '#444444', fontStyle: 'italic', textAlign: 'center', padding: '0 20px 8px', letterSpacing: '0.5px' },
  warrenBtn: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '9px 18px', borderRadius: 20, border: '1px solid #222222', background: 'rgba(0,0,0,0.4)', color: '#666666', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', flexShrink: 0, backdropFilter: 'blur(10px)', transition: 'all 0.2s' },
  menuBtn: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '9px 20px', borderRadius: 20, border: '1px solid #222222', background: 'rgba(0,0,0,0.4)', color: '#666666', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.2s', backdropFilter: 'blur(10px)' },
  mapBar: { height: 0 },
  mapBtn: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '9px 20px', borderRadius: 20, border: '1px solid #222222', background: 'rgba(0,0,0,0.4)', color: '#666666', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.2s', backdropFilter: 'blur(10px)' },
  newHoleBtn: { fontSize: 20, width: 42, height: 42, borderRadius: 20, border: '1px solid #222222', background: 'rgba(0,0,0,0.4)', color: '#555555', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', backdropFilter: 'blur(10px)' },
  warrenCount: { fontSize: 10, background: '#1a1a1a', color: '#444444', padding: '2px 7px', borderRadius: 10, letterSpacing: '0.5px' },
  main: { flex: 1, overflowY: 'auto', position: 'relative' },
  error: { margin: '20px 32px', padding: '14px 18px', borderRadius: 10, background: '#1a1111', border: '1px solid #3a1515', fontSize: 13, color: '#7eb8c9', lineHeight: 1.6 },
  inputBar: { display: 'flex', gap: 0, padding: '6px 20px 18px', background: 'transparent', flexShrink: 0 },
  input: { flex: 1, background: '#0d0d0d', border: '1px solid #1f1f1f', borderRadius: '14px 0 0 14px', padding: '14px 22px', fontSize: 15, color: '#e8e8e8', outline: 'none', fontFamily: 'Inter, sans-serif', userSelect: 'text', WebkitUserSelect: 'text', transition: 'all 0.2s', letterSpacing: '0.01em' },
  askBtn: { width: 50, height: 50, borderRadius: '0 14px 14px 0', border: 'none', background: '#1a1a1a', color: '#555555', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' },
};

